import React, { useState } from "react";
import { useT } from "@/i18n";

const DEPTHS_EN = ["Whisper", "Veil", "Linen", "Sand", "Stone", "Bronze", "Umber", "Clay", "Earth", "Forge", "Pitch", "Reveal"];
const DEPTHS_FR = ["Murmure", "Voile", "Lin", "Sable", "Pierre", "Bronze", "Ombre", "Argile", "Terre", "Forge", "Brai", "Révélation"];
const HEX = ["#e8e3dc", "#dcd4c8", "#cdc2b1", "#bcae99", "#a99882", "#9a8268", "#856a51", "#6e553f", "#5a4530", "#473522", "#33261a", "#1f1611"];

const EtchDepths = () => {
  const { t, lang } = useT();
  const names = lang === "fr" ? DEPTHS_FR : DEPTHS_EN;
  const [hover, setHover] = useState(null);
  const active = hover ?? 5;
  const cur = { n: active + 1, name: names[active], hex: HEX[active] };

  return (
    <section data-testid="etch-section" className="py-24 sm:py-32 bg-[#ece8df] border-y border-black/5">
      <div className="max-w-7xl mx-auto px-6 sm:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-end mb-16">
          <div className="md:col-span-7">
            <div className="overline mb-6">{t.etch.overline}</div>
            <h2 className="font-display text-4xl sm:text-6xl tracking-tight leading-[1.02]">
              {t.etch.h}<br />{t.etch.h2}
            </h2>
          </div>
          <div className="md:col-span-5">
            <p className="text-zinc-600 leading-relaxed">{t.etch.sub}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-10">
          <div
            className="md:col-span-5 aspect-[5/4] relative overflow-hidden transition-colors duration-700"
            style={{ background: cur.hex }}
            data-testid="etch-preview"
          >
            <div className="absolute inset-0 grain opacity-100" />
            <div className="absolute bottom-6 left-6 text-black/70">
              <div className="text-xs tracking-[0.3em] uppercase">{t.etch.depth} {String(cur.n).padStart(2, "0")}</div>
              <div className="font-display text-3xl mt-1">{cur.name}</div>
            </div>
          </div>
          <div className="md:col-span-7 grid grid-rows-[1fr_auto] gap-4">
            <div className="relative aspect-[16/8] overflow-hidden">
              <img
                src="/generated/macro_texture.png"
                alt="Macro view of exposed-aggregate decorative concrete texture"
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <div className="font-display text-zinc-400 num-display text-[5rem] sm:text-[7rem] leading-none">
                {String(cur.n).padStart(2, "0")}
              </div>
              <p className="text-zinc-600 max-w-md leading-relaxed">{t.etch.hint}</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-12 gap-2">
          {HEX.map((h, i) => (
            <button
              key={i}
              data-testid={`etch-swatch-${i + 1}`}
              onMouseEnter={() => setHover(i)}
              onFocus={() => setHover(i)}
              className={`etch-swatch aspect-square relative group ${i === active ? "ring-2 ring-[#ea7a1f] ring-offset-2 ring-offset-[#ece8df]" : ""}`}
              style={{ background: h }}
              aria-label={`Depth ${i + 1} ${names[i]}`}
            >
              <span className="absolute bottom-1 left-1.5 text-[10px] font-bold text-black/60">{String(i + 1).padStart(2, "0")}</span>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
};

export default EtchDepths;
